import { Component, Inject } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";

@Component({
  selector: "app-confirmation-dialog",
  templateUrl: "./confirmation-dialog.component.html",
  styleUrls: ["./confirmation-dialog.component.scss"],
})
export class ConfirmationDialogComponent {
  comment: string = "";
  title: string = "";
  makePublicAndPublish: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ConfirmationDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      action: string;
      request: any;
      isIncoming: boolean;
    }
  ) {}
  getDialogTitle(): string {
    const action = this.data.action;
    switch (action) {
      case "DiscardReview":
        return "Leave this page?";
      case "Take Down":
        return "Confirm Take Down";
      case "Mark as Fine":
        return "Confirm Mark as Fine";
      case "Approved":
        return "Approve Transfer";
      case "Rejected":
        return "Reject Transfer";
      case "Rework Requested":
        return "Request Rework";
      case "Rework Accepted":
        return "Accept Rework";
      case "Rework Rejected":
        return "Reject Rework";
      case "Cancel":
        return "Cancel Transfer";
      default:
        return "Confirm Action";
    }
  }

  getDialogTitleClass(): string {
    const action = this.data.action;
    switch (action) {
      case "DiscardReview":
        return "cancel-title";
      case "Take Down":
        return "reject-title";
      case "Mark as Fine":
        return "approve-title";
      case "Approved":
        return "approve-title";
      case "Rejected":
        return "reject-title";
      case "Rework Requested":
      case "Rework Accepted":
      case "Rework Rejected":
        return "rework-title";
      case "Cancel":
        return "cancel-title";
      default:
        return "";
    }
  }

  getEnhancedConfirmationMessage(): string {
    const action = this.data.action;
    const request = this.data.request;
    const contentTitle = request.contentTitle || "Untitled Content";

    // Simple stay/leave confirmation for discarding review/issues
    if (action === "DiscardReview") {
      const customMessage =
        (request && request.customMessage) ||
        "If you leave now, your changes will not be saved.";

      return `
        <div class="status-message cancel-message">
          <strong>${customMessage}</strong>
          <br><br>
          <div class="next-steps">
            <strong>Choose an option:</strong>
            <ul>
              <li><strong>Stay</strong> to keep this window open and continue editing.</li>
              <li><strong>Leave</strong> to close now and lose any unsaved issues or review.</li>
            </ul>
          </div>
        </div>
      `;
    }

    switch (action) {
      case "Take Down":
        return `
          <div class="status-message reject-message">
            <strong>You're about to TAKE DOWN this content.</strong>
            <br><br>
            <div class="next-steps">
              <strong>Next Steps:</strong>
              <ul>
                <li>"${contentTitle}" will be removed due to rule violation</li>
                <li>The content owner will be notified of this action</li>
                <li>This action is permanent and cannot be reversed</li>
              </ul>
            </div>
          </div>
        `;

      case "Mark as Fine":
        return `
          <div class="status-message approve-message">
            <strong>You're about to MARK this content as FINE.</strong>
            <br><br>
            <div class="next-steps">
              <strong>Next Steps:</strong>
              <ul>
                <li>"${contentTitle}" will remain available</li>
                <li>Report will be closed as no rule violation found</li>
              </ul>
            </div>
          </div>
        `;
      case "Approved":
        return `
          <div class="status-message approve-message">
            <strong>You're about to APPROVE this transfer request.</strong>
            <br><br>
            <div class="next-steps">
              <strong>Next Steps:</strong>
              <ul>
                <li>Ownership of "${contentTitle}" will be transferred from ${request.oldOwnerEmail} to ${request.newOwnerEmail}</li>
                <li>The new owner will receive immediate access to manage this content</li>
                <li>The original owner will lose all editing and management rights</li>
                <li>This action is permanent and cannot be reversed</li>
              </ul>
            </div>
          </div>
        `;

      case "Rejected":
        return `
          <div class="status-message reject-message">
            <strong>You're about to REJECT this transfer request.</strong>
            <br><br>
            <div class="next-steps">
              <strong>Next Steps:</strong>
              <ul>
                <li>The transfer request will be permanently rejected</li>
                <li>The original owner will retain full ownership of "${contentTitle}"</li>
                <li>The requesting user will be notified of the rejection</li>
                <li>A new transfer request can be submitted if needed</li>
              </ul>
            </div>
          </div>
        `;

      case "Rework Requested":
        return `
          <div class="status-message rework-message">
            <strong>You're about to REQUEST REWORK for this transfer.</strong>
            <br><br>
            <div class="next-steps">
              <strong>Next Steps:</strong>
              <ul>
                <li>The request will be returned to the sender for additional information</li>
                <li>The requesting user will need to provide clarification or missing details</li>
                <li>Once updated, the request will return to pending status for review</li>
                <li>The transfer process will continue from where it left off</li>
              </ul>
            </div>
          </div>
        `;

      case "Rework Accepted":
        return `
          <div class="status-message rework-message">
            <strong>You're about to ACCEPT the rework for this transfer.</strong>
            <br><br>
            <div class="next-steps">
              <strong>Next Steps:</strong>
              <ul>
                <li>The transfer request will return to pending status</li>
                <li>The request will be available for final review and decision</li>
                <li>All updated information will be incorporated into the request</li>
                <li>You can now approve, reject, or request further changes</li>
              </ul>
            </div>
          </div>
        `;

      case "Rework Rejected":
        return `
          <div class="status-message rework-message">
            <strong>You're about to REJECT the rework for this transfer.</strong>
            <br><br>
            <div class="next-steps">
              <strong>Next Steps:</strong>
              <ul>
                <li>The transfer request will be permanently rejected</li>
                <li>The original owner will retain full ownership of "${contentTitle}"</li>
                <li>The requesting user will be notified of the rejection</li>
                <li>A new transfer request can be submitted if needed</li>
              </ul>
            </div>
          </div>
        `;

      case "Cancel":
        return `
          <div class="status-message cancel-message">
            <strong>You're about to CANCEL this transfer request.</strong>
            <br><br>
            <div class="next-steps">
              <strong>Next Steps:</strong>
              <ul>
                <li>The transfer request will be permanently cancelled</li>
                <li>The original owner will retain full ownership of "${contentTitle}"</li>
                <li>All parties will be notified of the cancellation</li>
                <li>A new transfer request can be submitted if needed</li>
              </ul>
            </div>
          </div>
        `;

      default:
        return `Are you sure you want to ${action} this transfer request? Please review the details carefully.`;
    }
  }

  getStatusIcon(): string {
    const action = this.data.action;
    switch (action) {
      case "DiscardReview":
        return "warning_amber";
      case "Approved":
        return "check_circle";
      case "Rejected":
        return "cancel";
      case "Rework Requested":
      case "Rework Accepted":
      case "Rework Rejected":
        return "build";
      case "Cancel":
        return "block";
      default:
        return "help_outline";
    }
  }

  getIconClass(): string {
    const action = this.data.action;
    switch (action) {
      case "DiscardReview":
        return "cancel";
      case "Take Down":
        return "reject";
      case "Mark as Fine":
        return "approve";
      case "Approved":
        return "approve";
      case "Rejected":
        return "reject";
      case "Rework Requested":
      case "Rework Accepted":
      case "Rework Rejected":
        return "rework";
      case "Cancel":
        return "cancel";
      default:
        return "";
    }
  }

  getConfirmButtonClass(): string {
    const action = this.data.action;
    switch (action) {
      case "DiscardReview":
        return "cancel";
      case "Take Down":
        return "reject";
      case "Mark as Fine":
        return "approve";
      case "Approved":
        return "approve";
      case "Rejected":
        return "reject";
      case "Rework Requested":
      case "Rework Accepted":
      case "Rework Rejected":
        return "rework";
      case "Cancel":
        return "cancel";
      default:
        return "";
    }
  }

  getConfirmButtonIcon(): string {
    const action = this.data.action;
    switch (action) {
      case "DiscardReview":
        return "exit_to_app";
      case "Take Down":
        return "delete_forever";
      case "Mark as Fine":
        return "check";
      case "Approved":
        return "check";
      case "Rejected":
        return "close";
      case "Rework Requested":
        return "build";
      case "Rework Accepted":
        return "check_circle";
      case "Rework Rejected":
        return "cancel";
      case "Cancel":
        return "block";
      default:
        return "check";
    }
  }

  getConfirmButtonText(): string {
    const action = this.data.action;
    switch (action) {
      case "DiscardReview":
        return "Leave";
      case "Take Down":
        return "Take Down";
      case "Mark as Fine":
        return "Mark as Fine";
      case "Approved":
        return "Approve";
      case "Rejected":
        return "Reject Transfer";
      case "Rework Requested":
        return "Request Rework";
      case "Rework Accepted":
        return "Accept Rework";
      case "Rework Rejected":
        return "Reject Rework";
      case "Cancel":
        return "Cancel Transfer";
      default:
        return "Confirm";
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  titleChanged(): void {
    // Method to handle title changes - can be extended for validation if needed
    console.log('Transferee comment changed:', this.title);
  }

  onConfirm(): void {
    this.dialogRef.close({
      confirmed: true,
      comment: this.title || this.comment,
      makePublicAndPublish: this.makePublicAndPublish,
    });
  }

  onCancel(): void {
    this.dialogRef.close({
      confirmed: false,
    });
  }
}
